// UserButton.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

// Hooks
import useBaseAccess from 'hooks/useBaseAccess';

// Utilities
import { PhotoShape } from 'utilities/Enums';
import { formatRoleLabel } from 'services/bases/BaseRoleService';

// CSS
import './UserButton.css';

// Icons
import Icon from 'svg/Icon';

// Components
import UserSettingsModal from './UserSettingsModal';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';

/**
 * UserButton
 *
 * Renders a sidebar control for the signed-in user that opens account settings.
 *
 * @returns {ReactNode} UserButton.
 */
const UserButton = React.memo(() => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const { isSidebarRailCompact } = useUI();
    const { user } = useUser();
    const compact = Boolean(isSidebarRailCompact);

    // ----- HOOKS ----- //

    const { baseRole, roleDefinition, isRoleLoading } = useBaseAccess();

    // ----- STATE ----- //

    const [hovered, setHovered] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [settingsInitialTab, setSettingsInitialTab] = useState(null);

    // ----- MEMOS ----- //

    const userName = useMemo(() => {
        return String(
            user?.name
            || user?.title
            || user?.displayName
            || user?.data_profile?.name
            || user?.email
            || user?.data_profile?.email
            || ''
        ).trim();
    }, [
        user?.data_profile?.email,
        user?.data_profile?.name,
        user?.displayName,
        user?.email,
        user?.name,
        user?.title,
    ]);

    const userRoleLabel = useMemo(() => {
        if (isRoleLoading) return '';
        const customRolesByKey = baseRole && roleDefinition
            ? { [baseRole]: roleDefinition }
            : {};
        return formatRoleLabel(baseRole, customRolesByKey);
    }, [baseRole, isRoleLoading, roleDefinition]);

    // ----- HANDLERS ----- //

    /**
     * Handles click.
     */
    const handleClick = () => {
        setSettingsInitialTab(null);
        setSettingsModalOpen(true);
    };

    /**
     * Handles settings modal close.
     */
    const handleSettingsModalClose = () => {
        setSettingsModalOpen(false);
        setSettingsInitialTab(null);
    };

    if (!user) return null;

    // ----- RENDER ----- //

    return (
        <>
            <div
              className={[
                'user-button-container',
                compact ? 'is-rail-compact' : '',
              ].filter(Boolean).join(' ')}
            >
                {/* TOGGLE */}
                <div
                  className={[
                    'user-button-toggle',
                    compact ? 'is-rail-compact' : '',
                  ].filter(Boolean).join(' ')}
                  style={{
                        backgroundColor: hovered ? theme.buttonHoverBackgroundColor : 'transparent',
                    }}
                  title={compact ? (userName || 'Account') : undefined}
                  onClick={handleClick}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                >

                    {/* PHOTO */}
                    <div className="user-button-photo">
                        <ProfilePhoto
                          node={user}
                          borderWidth={0}
                          borderRadius={8}
                          size={36}
                          shape={PhotoShape.SQUARE}
                        />
                    </div>

                    {/* WRAPPER */}
                    {!compact ? (
                        <div className="user-button-text-wrapper">
                            {/* WRAPPER */}
                            <div className="user-button-title-wrapper">
                                {/* TITLE */}
                                <div
                                  className="user-button-title"
                                  style={{
                                        color: hovered ? theme.buttonHoverLabelColor : theme.foreground,
                                    }}
                                >
                                    {userName}
                                </div>
                            </div>

                            {/* LABEL */}
                            {userRoleLabel ? (
                                <div
                                  className="user-button-label"
                                  style={{
                                        color: hovered ? theme.buttonHoverLabelColor : theme.foregroundMuted,
                                    }}
                                >
                                    {userRoleLabel}
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* ARROW */}
                    {!compact ? (
                        <div className="user-button-arrow">
                            <Icon
                              iconKey="chevron_right"
                              size={16}
                              color={hovered ? theme.buttonHoverLabelColor : theme.foreground}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <UserSettingsModal
              isOpen={settingsModalOpen}
              setOpen={setSettingsModalOpen}
              initialTab={settingsInitialTab}
              onClose={handleSettingsModalClose}
            />
        </>
    );

});

// Dev Tools
UserButton.displayName = 'UserButton';

export default UserButton;
